import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import axiosInstance from "../../axiosInstance";
import Sidebar from "../components/Sidebar";
import Navbar from "../components/Navbar";
import { getJobPortalAuthConfig } from "../../helpers/jobPortalAuth";

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    stats: {},
    recentRecruiters: [],
    recentJobSeekers: [],
    recentJobs: [],
    recentApplications: [],
    pendingApprovalJobs: [],
  });

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const authConfig = await getJobPortalAuthConfig();
      const response = await axiosInstance.get(
        "/job-portal/admin/dashboard",
        authConfig
      );
      setData(response.data?.data || {});
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to load admin dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
  }, []);

  const stats = data.stats || {};

  const statCards = [
    ["Total Users", stats.totalUsers],
    ["Recruiters", stats.totalRecruiters],
    ["Job Seekers", stats.totalJobSeekers],
    ["Total Jobs", stats.totalJobs],
    ["Active Jobs", stats.activeJobs],
    ["Pending Approval", stats.pendingApprovalJobs],
    ["Approved Jobs", stats.approvedJobs],
    ["Rejected Jobs", stats.rejectedJobs],
    ["Applications", stats.totalApplications],
    ["Shortlisted", stats.shortlistedApplications],
    ["Rejected Applications", stats.rejectedApplications],
    ["Selected Applications", stats.selectedApplications],
    ["Subscription Orders", stats.totalSubscriptionOrders],
    ["Recruiter Subscriptions", stats.recruiterSubscriptionOrders],
    ["Job Seeker Subscriptions", stats.jobSeekerSubscriptionOrders],
    ["Paid Subscription Orders", stats.paidSubscriptionOrders],
  ];

  return (
    <>
      <Sidebar />
      <main className="content">
        <Navbar />
        <div className="py-4">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-4">
            <div>
              <h4 className="fw-bold mb-1">Admin Job Portal Dashboard</h4>
              <p className="text-muted small mb-0">
                Full platform visibility for recruiters, job seekers, jobs, approvals, and applications.
              </p>
            </div>
            <div className="d-flex gap-2 flex-wrap">
              <Link to="/all-recruiter" className="btn btn-outline-primary">
                Recruiters
              </Link>
              <Link to="/all-seeker" className="btn btn-outline-primary">
                Job Seekers
              </Link>
              <Link to="/all-job" className="btn btn-outline-primary">
                Jobs
              </Link>
              <Link to="/admin/subscription-orders" className="btn btn-outline-primary">
                Subscription Orders
              </Link>
              <Link to="/admin/applications" className="btn btn-primary">
                Applications
              </Link>
              <Link to="/admin/database-transfer" className="btn btn-outline-secondary">
                Import / Export
              </Link>
            </div>
          </div>

          <div className="row g-3 mb-4">
            {statCards.map(([label, value]) => (
              <div className="col-md-6 col-xl-3" key={label}>
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body">
                    <div className="small text-muted">{label}</div>
                    <div className="fs-3 fw-bold">{loading ? "--" : value || 0}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="row g-3 mb-4">
            <div className="col-md-3">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body">
                  <div className="small text-muted">New Recruiters This Month</div>
                  <div className="fs-3 fw-bold">{loading ? "--" : stats.newRecruitersThisMonth || 0}</div>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body">
                  <div className="small text-muted">New Job Seekers This Month</div>
                  <div className="fs-3 fw-bold">{loading ? "--" : stats.newJobSeekersThisMonth || 0}</div>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body">
                  <div className="small text-muted">Jobs Posted This Month</div>
                  <div className="fs-3 fw-bold">{loading ? "--" : stats.jobsPostedThisMonth || 0}</div>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body">
                  <div className="small text-muted">Applications This Month</div>
                  <div className="fs-3 fw-bold">{loading ? "--" : stats.applicationsThisMonth || 0}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="row g-3">
            <div className="col-xl-6">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="mb-0">Pending Approval Jobs</h6>
                    <Link to="/admin/job-approvals" className="btn btn-sm btn-outline-primary">
                      Review
                    </Link>
                  </div>
                  {loading ? (
                    <p className="text-muted mb-0">Loading...</p>
                  ) : data.pendingApprovalJobs?.length ? (
                    data.pendingApprovalJobs.map((job) => (
                      <div key={job.id} className="border-bottom py-2">
                        <div className="fw-semibold">{job.title}</div>
                        <div className="text-muted small">
                          {job.company?.name || "-"} | {job.recruiter?.name || "-"}
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-muted mb-0">No pending approval jobs.</p>
                  )}
                </div>
              </div>
            </div>

            <div className="col-xl-6">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body">
                  <h6 className="mb-3">Recent Jobs</h6>
                  {loading ? (
                    <p className="text-muted mb-0">Loading...</p>
                  ) : data.recentJobs?.length ? (
                    data.recentJobs.map((job) => (
                      <div key={job.id} className="border-bottom py-2">
                        <div className="fw-semibold">{job.title}</div>
                        <div className="text-muted small">
                          {job.company?.name || "-"} | {job.location?.display || "-"}
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-muted mb-0">No recent jobs.</p>
                  )}
                </div>
              </div>
            </div>

            <div className="col-xl-6">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body">
                  <h6 className="mb-3">Recent Recruiters</h6>
                  {loading ? (
                    <p className="text-muted mb-0">Loading...</p>
                  ) : data.recentRecruiters?.length ? (
                    data.recentRecruiters.map((user) => (
                      <div key={user.id} className="border-bottom py-2">
                        <div className="fw-semibold">{user.name}</div>
                        <div className="text-muted small">
                          {user.email || "-"} | {user.counts?.totalJobs || 0} jobs
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-muted mb-0">No recent recruiters.</p>
                  )}
                </div>
              </div>
            </div>

            <div className="col-xl-6">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body">
                  <h6 className="mb-3">Recent Job Seekers</h6>
                  {loading ? (
                    <p className="text-muted mb-0">Loading...</p>
                  ) : data.recentJobSeekers?.length ? (
                    data.recentJobSeekers.map((user) => (
                      <div key={user.id} className="border-bottom py-2">
                        <div className="fw-semibold">{user.name}</div>
                        <div className="text-muted small">
                          {user.email || "-"} | {user.counts?.totalApplications || 0} applications
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-muted mb-0">No recent job seekers.</p>
                  )}
                </div>
              </div>
            </div>

            <div className="col-12">
              <div className="card border-0 shadow-sm">
                <div className="card-body">
                  <h6 className="mb-3">Recent Applications</h6>
                  <div className="table-responsive">
                    <table className="table align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Applicant</th>
                          <th>Job</th>
                          <th>Recruiter</th>
                          <th>Status</th>
                          <th>Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loading ? (
                          <tr>
                            <td colSpan="5" className="text-center py-4 text-muted">
                              Loading...
                            </td>
                          </tr>
                        ) : data.recentApplications?.length ? (
                          data.recentApplications.map((application) => (
                            <tr key={application.id}>
                              <td>{application.applicant?.name || "-"}</td>
                              <td>{application.job?.title || "-"}</td>
                              <td>{application.job?.recruiter?.name || "-"}</td>
                              <td>{application.statusLabel || "-"}</td>
                              <td>
                                {application.appliedAt
                                  ? new Date(application.appliedAt).toLocaleDateString("en-IN")
                                  : "-"}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="5" className="text-center py-4 text-muted">
                              No recent applications.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default Dashboard;
